import React, { useState, useRef, useEffect } from 'react';
import { useLanguageProfile } from '../../contexts/LanguageProfileContext';

// Workspace-style switcher for the active language profile. Sits in the
// Sidebar between the user info block and the platform navigation — the
// active profile scopes which WordPress/Pinterest/Facebook/Threads accounts
// and content show up everywhere below it. Etsy is not affected.
export default function ProfileSwitcher() {
  const { profiles, activeProfile, setActiveProfile, loading } = useLanguageProfile();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (loading || profiles.length === 0) {
    return (
      <div className="px-3 py-2 border-b border-gray-200 flex-shrink-0">
        <div className="h-9 rounded-lg bg-gray-100 animate-pulse" />
      </div>
    );
  }

  const handleSelect = (profileId) => {
    if (profileId !== activeProfile?.id) setActiveProfile(profileId);
    setIsOpen(false);
  };

  return (
    <div className="px-3 py-2 border-b border-gray-200 flex-shrink-0" ref={containerRef}>
      <div className="relative">
        <button
          type="button"
          onClick={() => setIsOpen((prev) => !prev)}
          className="w-full flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2 hover:bg-gray-50 transition-colors"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          <span className="flex items-center gap-2 min-w-0">
            <span
              className="flex items-center justify-center rounded-md text-[10px] font-bold text-white flex-shrink-0"
              style={{ width: '26px', height: '18px', backgroundColor: '#166534' }}
            >
              {activeProfile?.language_code?.toUpperCase()}
            </span>
            <span className="text-sm font-medium text-gray-800 truncate">
              {activeProfile?.name || 'Profile'}
            </span>
          </span>
          <svg
            className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {isOpen && (
          <ul
            role="listbox"
            className="absolute left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-hidden"
          >
            {profiles.map((profile) => {
              const isSelected = profile.id === activeProfile?.id;
              return (
                <li key={profile.id} role="option" aria-selected={isSelected}>
                  <button
                    type="button"
                    onClick={() => handleSelect(profile.id)}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors ${isSelected ? 'bg-gray-50 font-medium' : ''}`}
                  >
                    <span
                      className="flex items-center justify-center rounded-md text-[10px] font-bold text-white flex-shrink-0"
                      style={{ width: '26px', height: '18px', backgroundColor: isSelected ? '#166534' : '#9CA3AF' }}
                    >
                      {profile.language_code.toUpperCase()}
                    </span>
                    <span className="text-gray-800">{profile.name}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
